import { Icon20ArrowLeftOutline, Icon20ArrowRightOutline } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { type RefObject, memo, useCallback, useEffect, useState } from 'react';
import { name } from 'reactApp/modules/aiTools/helpers/constants';
import { sendDwhButton } from 'reactApp/modules/dwh/dwh.helpers';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipAppearance, ETooltipPlacement } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { Navigation, Pagination } from 'swiper';
import { type SwiperRef, Swiper, SwiperSlide } from 'swiper/react';

import styles from './BackgroundSelector.css';
import type { ItemType } from './BackgroundSelector.helpers';
import { ColorsWidget } from './ColorsWidget';

export const backgroundSelectorId = 'ai-rmvbgrnd';

interface Props {
    items: (ItemType | ItemType[])[];
    activeBackgroundId: string;
    target: RefObject<HTMLDivElement>;
    isNewbackground?: boolean;

    onSelectItem(id: string): void;

    onSelectFromCloud(): void;

    onClose(): void;
}

export const BackgroundSelector = memo<Props>(function BackgroundSelector({
    target,
    items,
    activeBackgroundId,
    onSelectFromCloud,
    onSelectItem,
    isNewbackground = false,
    onClose,
}) {
    const [swiperInstance, setSwiperInstance] = useState<SwiperRef['swiper'] | null>(null);
    const [colorsWidth, setColorWidth] = useState(0);

    const handleSelectItem = useCallback(
        (event) => {
            if (!event?.target?.id) {
                return;
            }
            onSelectItem(event.target.id);
        },
        [onSelectItem]
    );

    const updateSwiper = useCallback((width: number) => {
        setColorWidth(width);
    }, []);

    useEffect(() => {
        swiperInstance?.update();
    }, [colorsWidth, swiperInstance]);

    const handleNavClick = useCallback(
        (label: 'next' | 'prev') =>
            sendDwhButton({
                action: 'click',
                name,
                button_name: label,
            }),
        []
    );

    return (
        <FloatingTooltip
            target={target}
            placement={ETooltipPlacement.top}
            qaId={backgroundSelectorId}
            appearance={ETooltipAppearance.dark}
            className={styles.addBackgroundPopup}
            contentClassName={styles.addBackgroundPopupContent}
            leftOffset={isNewbackground ? -68 : -40}
            topOffset={4}
            closable={false}
            usePortal
            hideArrow
            closeOnOutsideClick={true}
            autoUpdateOnResize
            onClose={onClose}
            allowFlip
        >
            <>
                <div className={styles.tabBlock}>
                    <div className={styles.tab} onClick={onSelectFromCloud}>
                        Из Облака
                    </div>
                    <div className={styles.arrows}>
                        <div className={styles.arrowPrev} onClick={() => handleNavClick('prev')}>
                            <Icon20ArrowLeftOutline width={16} height={16} />
                        </div>
                        <div className={styles.arrowNext} onClick={() => handleNavClick('next')}>
                            <Icon20ArrowRightOutline width={16} height={16} />
                        </div>
                    </div>
                </div>
                <div className={styles.items} onClick={handleSelectItem}>
                    <Swiper
                        simulateTouch
                        loop={false}
                        modules={[Navigation, Pagination]}
                        slidesPerView="auto"
                        spaceBetween={6}
                        className={styles.swiper}
                        onSwiper={setSwiperInstance}
                        navigation={{
                            nextEl: `.${styles.arrowNext}`,
                            prevEl: `.${styles.arrowPrev}`,
                            disabledClass: styles.arrowDisabled,
                        }}
                    >
                        {items.map((item) => {
                            if (Array.isArray(item)) {
                                return (
                                    <SwiperSlide
                                        key="colors"
                                        className={styles.slide}
                                        style={{ width: colorsWidth ? `${colorsWidth}px` : 'auto' }}
                                    >
                                        <ColorsWidget
                                            key="colors_widget"
                                            items={item}
                                            activeBackgroundId={activeBackgroundId}
                                            onCollapseChanged={updateSwiper}
                                        />
                                    </SwiperSlide>
                                );
                            }
                            return (
                                <SwiperSlide key={item.id} className={styles.slide}>
                                    <div
                                        id={item.id}
                                        key={item.id}
                                        className={classNames(styles.item, { [styles.active]: item.id === activeBackgroundId })}
                                        style={{
                                            background: item.type === 'color' ? item.id : undefined,
                                            backgroundImage: item.backgroundImageThumb ? `url(${item.backgroundImageThumb})` : 'none',
                                        }}
                                    />
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>
            </>
        </FloatingTooltip>
    );
});

BackgroundSelector.displayName = 'BackgroundSelector';
